import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { MultiSegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import {
  BaseTemplate,
  IBaseTemplate,
  MultiSelectDynamic,
} from 'core-components/library/multiSelect'
import { useStaticCrud } from 'core-components/types/helpers'
import type { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../../configs/translations'
import { setCockpitFilterPartnerClasses } from '../../../../../../model'
import {
  selectCockpitFilterPartnerClasses,
  selectCockpitPartnerClassOptions,
} from '../../../../../../model/selectors'
import { CockpitSegmentedControlAccent } from '../../../../../components/cockpitSegmentedControlAccent'
import { CockpitFilterLabel } from '../../../cockpitFilterLabel/CockpitFilterLabel'

import styles from './CockpitPartnerClassFilter.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.partnerClass

export const CockpitPartnerClassFilter = ({ dropdown = false }: { dropdown?: boolean }) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const selectedPartnerClasses = useSelector(selectCockpitFilterPartnerClasses)
  const partnerClassOptions = useSelector(selectCockpitPartnerClassOptions)

  const handleSelect = useCallback(
    (partnerClasses: string[]) => {
      dispatch(setCockpitFilterPartnerClasses(partnerClasses))
    },
    [dispatch]
  )

  const source = useStaticCrud<IP360PartnerFlags>({
    data: partnerClassOptions,
    keyField: 'PartnerFlag',
    displayField: 'Title',
  })
  const titleByPartnerFlag = useMemo(
    () => new Map(partnerClassOptions.map(({ PartnerFlag, Title }) => [PartnerFlag, Title])),
    [partnerClassOptions]
  )
  const renderSelectedPartnerClass = useCallback(
    (props: IBaseTemplate<(typeof partnerClassOptions)[number]>) => (
      <BaseTemplate
        {...props}
        className={styles.chip}
        customValueRender={titleByPartnerFlag.get(props.item.PartnerFlag) ?? props.item.Title}
      />
    ),
    [titleByPartnerFlag]
  )

  if (dropdown) {
    return (
      <div className={styles.root}>
        <CockpitFilterLabel text={KEYS.label} placeholderOnly />
        <MultiSelectDynamic
          source={source}
          caption="Title"
          value={selectedPartnerClasses}
          onChange={handleSelect}
          placeholder={t(KEYS.label)}
          closeOnSelect={false}
          classes={{ inputClasses: { leftSide: styles['chips-line'] } }}
          itemTemplate={renderSelectedPartnerClass}
        />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <Translation text={KEYS.label} span />
      <CockpitSegmentedControlAccent>
        <MultiSegmentedControl
          useItemKeys
          selectedItems={selectedPartnerClasses}
          onMultiItemSelect={handleSelect}
          size="small"
          variant="veneer"
          backgroudn="bg-white"
        >
          {partnerClassOptions.map((flag) => (
            <MultiSegmentedControl.Item key={flag.PartnerFlag} itemKey={flag.PartnerFlag}>
              <span>{flag.Title}</span>
            </MultiSegmentedControl.Item>
          ))}
        </MultiSegmentedControl>
      </CockpitSegmentedControlAccent>
    </div>
  )
}
